import { z } from 'zod';

import {
  type EditableLanding,
  LANDING_CHANNEL_PLATFORMS,
  LANDING_LOGO_SIZES,
  LANDING_PHOTO_FRAMES,
  LANDING_TERM_ICONS,
  type LandingDocument,
  type LandingEnglish,
  type LandingImage,
  type LandingSectionKey,
  type PublishedLanding,
} from '../model/types';
import { DEFAULT_LANDING_THEME } from '../model/theme';

/**
 * Validated at the boundary (CLAUDE.md §7.4). These check the *shape* the
 * contract promises, not the length limits: those are the domain's to enforce
 * on write, and a response is not the place to second-guess them.
 */
const noticeSchema = z.object({
  enabled: z.boolean(),
  barText: z.string(),
  title: z.string(),
  body: z.string(),
  dateLabel: z.string(),
  showOnBooking: z.boolean(),
});

/**
 * «پیام پیش از نوبت‌گیری» — added after the first documents were stored, so a
 * document without it reads as off rather than as a broken response.
 */
const bookingGateSchema = z
  .object({
    enabled: z.boolean(),
    title: z.string(),
    body: z.string(),
    buttonLabel: z.string(),
  })
  .default({ enabled: false, title: '', body: '', buttonLabel: '' });

const contactSchema = z.object({
  adminContactUrl: z.string().nullable(),
  adminContactText: z.string(),
  // added after the first documents were stored; absent reads as no photo
  adminPhotoId: z.string().nullable().default(null),
  channelHandle: z.string(),
  channels: z.array(z.object({ platform: z.enum(LANDING_CHANNEL_PLATFORMS), url: z.string() })),
  phones: z.array(z.string()),
  phoneHours: z.string(),
});

/** Hex strings as stored; the contrast rules are the domain's to enforce on write. */
export const landingThemeSchema = z.object({
  primaryColor: z.string(),
  backgroundColor: z.string(),
  footerColor: z.string(),
});

const shown = (): z.ZodDefault<z.ZodBoolean> => z.boolean().default(true);

/** «نمایش در صفحه» — every key optional; an absent one is shown. */
const sectionsSchema = z
  .object({
    heroFacts: shown(),
    aboutDoctor: shown(),
    aboutUs: shown(),
    bookingTerms: shown(),
    bookingGuide: shown(),
    faq: shown(),
    contact: shown(),
  } satisfies Record<LandingSectionKey, z.ZodDefault<z.ZodBoolean>>)
  .default({});

const aboutDoctorSchema = z.object({
  heading: z.string(),
  bio: z.string(),
  credentials: z.array(z.object({ label: z.string(), value: z.string() })),
  services: z.array(z.string()),
});

const aboutUsSchema = z.object({
  heading: z.string(),
  intro: z.string(),
  hours: z.array(z.object({ day: z.string(), value: z.string() })),
  routes: z.array(z.object({ label: z.string(), url: z.string() })),
});

const termsSchema = z.array(
  z.object({ icon: z.enum(LANDING_TERM_ICONS), title: z.string(), body: z.string() }),
);

const guideSchema = z.array(z.object({ title: z.string(), body: z.string() }));

const faqSchema = z.array(
  // a question stored before گروه‌بندی existed belongs to no group
  z.object({ group: z.string().default(''), question: z.string(), answer: z.string() }),
);

const emptyEnglish = () =>
  ({
    enabled: false,
    addressLine: '',
    notice: { barText: '', title: '', body: '', dateLabel: '' },
    bookingGate: { title: '', body: '', buttonLabel: '' },
    labReviewGate: { title: '', body: '', buttonLabel: '' },
    consultationGate: { title: '', body: '', buttonLabel: '' },
    hero: {
      eyebrow: '',
      doctorName: '',
      specialty: '',
      city: '',
      intro: '',
      badgeTitle: '',
      badgeText: '',
    },
    adBanner: { alt: '' },
    aboutDoctor: { heading: '', bio: '', credentials: [], services: [] },
    aboutUs: { heading: '', intro: '', hours: [], routes: [] },
    contact: { adminContactText: '', phoneHours: '' },
    footer: { tagline: '', copyright: '', legalNote: '' },
    seo: { title: '', description: '' },
    bookingTerms: [],
    bookingGuide: [],
    faq: [],
  }) satisfies LandingEnglish;

/** «نسخه انگلیسی» — a document stored before English existed has none, switched off. */
const englishSchema = z
  .object({
    enabled: z.boolean(),
    addressLine: z.string(),
    notice: z.object({
      barText: z.string(),
      title: z.string(),
      body: z.string(),
      dateLabel: z.string(),
    }),
    bookingGate: z.object({ title: z.string(), body: z.string(), buttonLabel: z.string() }),
    labReviewGate: z
      .object({ title: z.string(), body: z.string(), buttonLabel: z.string() })
      .default({ title: '', body: '', buttonLabel: '' }),
    consultationGate: z
      .object({ title: z.string(), body: z.string(), buttonLabel: z.string() })
      .default({ title: '', body: '', buttonLabel: '' }),
    hero: z.object({
      eyebrow: z.string(),
      doctorName: z.string(),
      specialty: z.string(),
      city: z.string(),
      intro: z.string(),
      badgeTitle: z.string(),
      badgeText: z.string(),
    }),
    adBanner: z.object({ alt: z.string() }),
    aboutDoctor: aboutDoctorSchema,
    aboutUs: aboutUsSchema,
    contact: z.object({ adminContactText: z.string(), phoneHours: z.string() }),
    footer: z.object({ tagline: z.string(), copyright: z.string(), legalNote: z.string() }),
    seo: z.object({ title: z.string(), description: z.string() }),
    bookingTerms: termsSchema,
    bookingGuide: guideSchema,
    faq: faqSchema,
  })
  .default(emptyEnglish);

export const landingDocumentSchema = z.object({
  notice: noticeSchema,
  bookingGate: bookingGateSchema,
  labReviewGate: bookingGateSchema,
  consultationGate: bookingGateSchema,
  hero: z.object({
    eyebrow: z.string(),
    doctorName: z.string(),
    specialty: z.string(),
    city: z.string(),
    intro: z.string(),
    doctorPhotoId: z.string().nullable(),
    badgeTitle: z.string(),
    badgeText: z.string(),
    // a document stored before the choice existed reads as the rectangle
    photoFrame: z.enum(LANDING_PHOTO_FRAMES).default('rounded'),
    // a document stored before the button existed reads as «off»
    consultationCta: z.boolean().default(false),
  }),
  adBanner: z.object({
    enabled: z.boolean(),
    imageId: z.string().nullable(),
    linkUrl: z.string().nullable(),
    alt: z.string(),
  }),
  aboutDoctor: aboutDoctorSchema,
  aboutUs: aboutUsSchema,
  bookingTerms: termsSchema,
  bookingGuide: guideSchema,
  faq: faqSchema,
  contact: contactSchema,
  footer: z.object({
    tagline: z.string(),
    copyright: z.string(),
    legalNote: z.string(),
    // the footer photo and eNamad came later; absent reads as none
    photoId: z.string().nullable().default(null),
    enamadId: z.string().default(''),
    enamadCode: z.string().default(''),
  }),
  seo: z.object({
    title: z.string(),
    description: z.string(),
    ogImageId: z.string().nullable(),
  }),
  // a document stored before رنگ‌بندی existed reads as today's design
  theme: landingThemeSchema.default(DEFAULT_LANDING_THEME),
  // …and one stored before logos existed, as having none
  branding: z
    .object({
      siteLogoId: z.string().nullable(),
      bookingLogoId: z.string().nullable(),
      footerLogoId: z.string().nullable().default(null),
      logoSize: z.enum(LANDING_LOGO_SIZES).default('medium'),
      faviconId: z.string().nullable().default(null),
    })
    .default({
      siteLogoId: null,
      bookingLogoId: null,
      footerLogoId: null,
      logoSize: 'medium',
      faviconId: null,
    }),
  // …and one stored before sections could be hidden, as showing them all
  sections: sectionsSchema,
  en: englishSchema,
});

const imageRefSchema = z.object({
  url: z.string(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
});

export const landingImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  mime: z.string(),
  width: z.number().int().nonnegative(),
  height: z.number().int().nonnegative(),
  bytes: z.number().int().nonnegative(),
});

export const editableLandingSchema = z.object({
  version: z.number().int().nonnegative(),
  content: landingDocumentSchema,
  images: z.array(landingImageSchema),
  published: z
    .object({
      version: z.number().int().nonnegative(),
      publishedAt: z.string().datetime({ offset: true }).nullable(),
    })
    .nullable(),
  hasUnpublishedChanges: z.boolean(),
});

/** A saved draft, and a discarded one: the draft's new version. */
export const landingSavedSchema = z.object({ version: z.number().int().nonnegative() });

export const landingPublishedSchema = z.object({
  publishedVersion: z.number().int().nonnegative(),
  publishedAt: z.string().datetime({ offset: true }),
});

/** The preview is a whole HTML document, not an envelope. */
export const landingPreviewSchema = z.string().min(1);

/**
 * The published view, narrowed to what the booking app reads. The rest of the
 * view (hero, FAQ, SEO…) belongs to the server-rendered landing and is
 * stripped here, so a change to a part this app never shows cannot take the
 * notice down with it.
 */
export const publishedLandingSchema = z.object({
  notice: noticeSchema,
  contact: contactSchema,
  location: z.object({
    addressLine: z.string().nullable(),
    mapUrl: z.string().nullable(),
  }),
  bookingUrl: z.string(),
  // «پیش از پرداخت» windows; a server from before them sends none
  labReviewGate: bookingGateSchema,
  consultationGate: bookingGateSchema,
  isPublished: z.boolean(),
  // not read by the booking app (it is never themed); accepted when present
  theme: landingThemeSchema.optional(),
  // a server from before logos existed sends none
  branding: z.object({ bookingLogo: imageRefSchema.nullable() }).default({ bookingLogo: null }),
  /** Null until the clinic has published once — the page is still the default copy. */
  updatedAt: z.string().datetime({ offset: true }).nullable(),
});

export function toLandingDocument(dto: z.infer<typeof landingDocumentSchema>): LandingDocument {
  return dto;
}

export function toLandingImage(dto: z.infer<typeof landingImageSchema>): LandingImage {
  return dto;
}

export function toEditableLanding(dto: z.infer<typeof editableLandingSchema>): EditableLanding {
  return {
    version: dto.version,
    content: toLandingDocument(dto.content),
    images: dto.images.map(toLandingImage),
    published:
      dto.published === null
        ? null
        : {
            version: dto.published.version,
            publishedAt:
              dto.published.publishedAt === null ? null : new Date(dto.published.publishedAt),
          },
    hasUnpublishedChanges: dto.hasUnpublishedChanges,
  };
}

export function toPublishedLanding(dto: z.infer<typeof publishedLandingSchema>): PublishedLanding {
  return {
    notice: dto.notice,
    contact: dto.contact,
    location: dto.location,
    bookingUrl: dto.bookingUrl,
    bookingLogo: dto.branding.bookingLogo,
    labReviewGate: dto.labReviewGate,
    consultationGate: dto.consultationGate,
    isPublished: dto.isPublished,
    updatedAt: dto.updatedAt === null ? null : new Date(dto.updatedAt),
  };
}
